import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { APIProvider } from '@vis.gl/react-google-maps'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Search,
  MapPin,
  ExternalLink,
  Sparkles,
  CalendarDays,
  Users,
  Building2,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ChevronRight,
  Trash2,
  Check,
  AlertTriangle,
  Plus,
  Clock,
  Info,
  Mail,
  Globe,
  Shield,
  Monitor,
  DollarSign,
  Layout,
  MessageSquare,
  Star,
  Bookmark,
} from 'lucide-react'
import { Button } from '~/client/components/ui/button'
import { Input } from '~/client/components/ui/input'
import { Textarea } from '~/client/components/ui/textarea'
import { Badge } from '~/client/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/client/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/client/components/ui/collapsible'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/client/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '~/client/components/ui/popover'
import { useAuthStore } from '~/client/common/stores/auth'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/client/components/ui/select'
import SortableRecsList from '~/client/components/admin-recs/SortableRecsList'
import { Grip } from 'lucide-react'
import { supabaseClient } from '~/server/common/supabase/client'
import { typesenseClient } from '~/client/common/typesense/client'
import { buildImageUrl } from '~/shared/common/image-url'
import { formatUtcDateLongFromUnknown } from '~/shared/common/date'
import { DateTime } from 'luxon'
import type { RevelRec } from '~/shared/recs/recs.types'
import { parseRevelRecsAdminStatus, staffUserDisplayName, REVEL_RECS_ADMIN_STATUSES } from '~/shared/recs/recs.types'
import type { RevelRecsAdminStatus, StaffUser } from '~/shared/recs/recs.types'
import { posthog, PostHogEvent } from '~/client/common/posthog'
import ListingsFiltersPanel from '~/client/components/browse-view/filters/ListingsFiltersPanel'
import ListingsResultsPanel from '~/client/components/browse-view/results/ListingsResultsPanel'
import { ListingsMap } from '~/client/components/browse-view/map/ListingsMap'
import { useListingsStore } from '~/client/common/stores/listings'

export const Route = createFileRoute('/admin/recs')({
  component: RecsAdminPage,
  validateSearch: (search: Record<string, unknown>) => ({
    eventId: typeof search.eventId === 'string' || typeof search.eventId === 'number' ? String(search.eventId) : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
    status: typeof search.status === 'string' ? search.status : undefined,
    assignees: typeof search.assignees === 'string' ? search.assignees : undefined,
    locations: typeof search.locations === 'string' ? search.locations : undefined,
    companies: typeof search.companies === 'string' ? search.companies : undefined,
    eventDate: typeof search.eventDate === 'string' ? search.eventDate : undefined,
    sortKey: typeof search.sortKey === 'string' ? search.sortKey : undefined,
    sortDir: typeof search.sortDir === 'string' ? search.sortDir : undefined,
  }),
})

// ── API helpers ──────────────────────────────────────────────────────────────

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabaseClient.auth.getSession()
  if (!session) throw new Error('Not authenticated')
  return { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }
}

async function apiGet<T = unknown>(action: string, params: Record<string, string> = {}): Promise<T> {
  const headers = await getAuthHeaders()
  const qs = new URLSearchParams({ action, ...params })
  const res = await fetch(`/api/adminRecsV2?${qs}`, { headers })
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json() as Promise<T>
}

async function apiPost<T = unknown>(body: Record<string, unknown>): Promise<T> {
  const headers = await getAuthHeaders()
  const res = await fetch('/api/adminRecsV2', { method: 'POST', headers, body: JSON.stringify(body) })
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json() as Promise<T>
}

// ── Space resolution (for recs display) ─────────────────────────────────────

interface ResolvedSpace {
  id: string
  name: string
  venueName: string | null
  city: string | null
  address: string | null
  imageUrl: string | null
  rating: number | null
  cuisine: string | null
  website: string | null
}

// Shape returned by `eventDetail` API — minimal projection of fields actually
// read inside EventDetailView, BriefTab, RecommendationsTab, SearchTab.
// Backend `crm_eventspacelist` has many more columns; we narrow here on purpose
// so adding a UI field forces an explicit type extension.
interface EventDateRow {
  start_date: string | null
  start_time: string | null
  end_time: string | null
}
interface EventVenueReferenceRow {
  id: number
  venue_id: number
  created_at: string | null
  venue: {
    id: number
    name: string | null
    city: string | null
    display_address: string | null
    rating_google: number | null
    website: string | null
    create_source: string | null
    first_space_id: number | null
    image_upload: string | null
  } | null
}
interface EventDetailData {
  id: number
  name: string | null
  location: string | null
  num_attendees: number | null
  event_type: string | null
  event_format: string | null
  space_type: string[] | null
  privacy_requirement: string | null
  amenities_requirements: string[] | null
  media_requirements: string[] | null
  budget_per_person: number | null
  budget_total: number | null
  display_json: Record<string, unknown> | null
  venue_group_id: number | null
  created_at: string | null
  revel_recs_brief: string | null
  revel_recs_brief_tags: string[] | null
  revel_recs_brief_submitted_at: string | null
  revel_recs_status: string | null
  revel_recs_admin_assignee_id: number | null
  creator: { id: number; name: string | null; email: string | null; first_name: string | null; last_name: string | null; title: string | null } | null
  entity: { id: number; name: string | null; type: string | null } | null
  dates: EventDateRow[]
  venue_references: EventVenueReferenceRow[]
}

interface ShortlistItem {
  id: number
  space_id: number | null
  created_at: string | null
  order: number | null
}

// Errors propagate to the caller so a transient failure can free the requested
// IDs for retry. A successful response with empty hits is still "resolved" and
// stays out of retry — we don't want to loop on a space that genuinely doesn't
// exist in Typesense.
async function resolveSpacesByIds(spaceIds: number[]): Promise<Map<number, ResolvedSpace>> {
  const map = new Map<number, ResolvedSpace>()
  if (spaceIds.length === 0) return map
  const response = await typesenseClient.multiSearch.perform({
    searches: [{
      collection: 'spaces',
      q: '*',
      filter_by: `id:=[${spaceIds.join(',')}]`,
      per_page: spaceIds.length,
      include_fields: 'id,name,$venues(name,city,display_address,images,rating_google,cuisines,website,google_place_id)',
    }],
  })
  const hits = (response.results[0] as any)?.hits ?? []
  for (const hit of hits) {
    const doc = hit.document as any
    const venue = doc.venues ?? {}
    const venueImages = Array.isArray(venue.images) ? venue.images : []
    const firstImage = venueImages[0]?.upload || null
    const cuisines = Array.isArray(venue.cuisines) ? venue.cuisines : []
    map.set(Number(doc.id), {
      id: String(doc.id),
      name: String(doc.name || ''),
      venueName: venue.name ? String(venue.name) : null,
      city: venue.city ? String(venue.city) : null,
      address: venue.display_address ? String(venue.display_address) : null,
      imageUrl: firstImage ? buildImageUrl(firstImage, { w: 120, h: 120 }) : null,
      rating: typeof venue.rating_google === 'number' ? venue.rating_google : null,
      cuisine: cuisines[0]?.name || null,
      website: venue.website ? String(venue.website) : null,
    })
  }
  return map
}

// ── Space resolution (for client shortlist) ──────────────────────────────────

async function resolveShortlistSpaces(spaceIds: number[]): Promise<Map<number, { name: string; venueName: string; city: string }>> {
  const map = new Map<number, { name: string; venueName: string; city: string }>()
  if (spaceIds.length === 0) return map
  const response = await typesenseClient.multiSearch.perform({
    searches: [{
      collection: 'spaces',
      q: '*',
      filter_by: `id:=[${spaceIds.join(',')}]`,
      per_page: spaceIds.length,
      include_fields: 'id,name,$venues(name,city)',
    }],
  })
  const hits = (response.results[0] as any)?.hits ?? []
  for (const hit of hits) {
    const doc = hit.document as any
    const venue = doc.venues ?? {}
    map.set(Number(doc.id), {
      name: doc.name || '',
      venueName: venue.name || '',
      city: venue.city || '',
    })
  }
  return map
}

// ── Status helpers ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<RevelRecsAdminStatus, { label: string; bg: string; text: string }> = {
  brief_submitted: { label: 'Brief Submitted', bg: 'bg-gray-100', text: 'text-gray-600' },
  in_progress: { label: 'In Progress', bg: 'bg-amber-100', text: 'text-amber-700' },
  published: { label: 'Published', bg: 'bg-emerald-100', text: 'text-emerald-700' },
}

function getStatusConfig(status: RevelRecsAdminStatus) {
  return STATUS_CONFIG[status]
}

// ── Main component ───────────────────────────────────────────────────────────

function RecsAdminPage() {
  const search = Route.useSearch()
  const { eventId, tab } = search
  const navigate = useNavigate({ from: Route.fullPath })

  if (eventId) {
    return <EventDetailView eventId={eventId} tab={tab || 'brief'} />
  }

  return (
    <QueueView
      initialStatus={search.status}
      initialAssignees={search.assignees}
      initialLocations={search.locations}
      initialCompanies={search.companies}
      initialEventDate={search.eventDate}
      initialSortKey={search.sortKey}
      initialSortDir={search.sortDir}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// QUEUE VIEW
// ═══════════════════════════════════════════════════════════════════════════════

interface QueueItem {
  id: number
  name: string
  location: string | null
  created_at: string
  brief_submitted_at: string | null
  revel_recs_status: RevelRecsAdminStatus
  revel_recs_admin_assignee_id: number | null
  assignee_name: string | null
  entity_name: string | null
  creator_name: string | null
  creator_email: string | null
  event_date: string | null
  // Active recs only (for "N recs published" SLA cell)
  rec_count: number
  // Earliest active-rec updated_at (proxy for when admin published); null if not published
  published_at: string | null
}

// ── Location parsing ────────────────────────────────────────────────────────
// Backend `location` field stores the full venue address. For the column filter
// we group by "City, State" so multiple addresses in the same city collapse into
// one entry. Heuristic: country is last segment, state second-to-last, city third-to-last.
// Falls back to the raw string when the format doesn't match.

function extractCityState(location: string | null | undefined): string | null {
  if (!location) return null
  const parts = location.split(',').map((p) => p.trim()).filter(Boolean)
  if (parts.length >= 3) {
    const city = parts[parts.length - 3]
    const state = parts[parts.length - 2]
    return `${city}, ${state}`
  }
  return location
}

// ── SLA cell helper (S1.7) ───────────────────────────────────────────────────
// Pure function — no hooks, no useEffect. Luxon for date math only.

function formatSlaElapsed(briefSubmittedAt: string, status: RevelRecsAdminStatus, recCount: number): string {
  if (status === 'published') return `${recCount} rec${recCount === 1 ? '' : 's'} published`
  const totalHours = DateTime.now().diff(DateTime.fromISO(briefSubmittedAt), 'hours').hours
  const hours = Math.floor(totalHours)
  const minutes = Math.floor((totalHours - hours) * 60)
  return `${hours}h ${minutes}m`
}

// Time-to-resolve for published events: brief_submitted_at → published_at.
// Returns { text, withinSla } so the renderer can color the duration by whether
// the 48h promise was kept.
function formatResolution(briefSubmittedAt: string, publishedAt: string): { text: string; withinSla: boolean } {
  const totalHours = DateTime.fromISO(publishedAt).diff(DateTime.fromISO(briefSubmittedAt), 'hours').hours
  const hours = Math.floor(totalHours)
  const minutes = Math.floor((totalHours - hours) * 60)
  return { text: `${hours}h ${minutes}m`, withinSla: totalHours <= 48 }
}

// SLA tier drives the color cue. Promise = 48h. Amber from 24h, red past 48h.
type SlaTier = 'published' | 'ok' | 'warn' | 'breach'
function getSlaTier(briefSubmittedAt: string, status: RevelRecsAdminStatus): SlaTier {
  if (status === 'published') return 'published'
  const hours = DateTime.fromISO(briefSubmittedAt).diffNow('hours').negate().hours
  if (hours >= 48) return 'breach'
  if (hours >= 24) return 'warn'
  return 'ok'
}

const SLA_TIER_CLASS: Record<SlaTier, string> = {
  published: 'text-muted-foreground',
  ok: 'text-muted-foreground',
  warn: 'text-amber-600 font-medium',
  breach: 'text-red-600',
}

// ── Column resize ───────────────────────────────────────────────────────────

type ColKey = 'event' | 'company' | 'owner' | 'location' | 'created' | 'submitted' | 'assigned' | 'status' | 'sla'
const COL_ORDER: ColKey[] = ['event', 'company', 'owner', 'location', 'created', 'submitted', 'assigned', 'status', 'sla']
const DEFAULT_COL_WIDTHS: Record<ColKey, number> = {
  event: 280, company: 140, owner: 140, location: 180,
  created: 130, submitted: 130, assigned: 130, status: 110, sla: 130,
}
const MIN_COL_WIDTHS: Record<ColKey, number> = {
  event: 180, company: 90, owner: 90, location: 110,
  created: 90, submitted: 90, assigned: 90, status: 90, sla: 80,
}
const COL_WIDTHS_STORAGE_KEY = 'admin-recs-col-widths-v1'

function loadStoredWidths(): Record<ColKey, number> {
  if (typeof window === 'undefined') return DEFAULT_COL_WIDTHS
  try {
    const raw = window.localStorage.getItem(COL_WIDTHS_STORAGE_KEY)
    if (!raw) return DEFAULT_COL_WIDTHS
    const parsed = JSON.parse(raw) as Partial<Record<ColKey, number>>
    return { ...DEFAULT_COL_WIDTHS, ...parsed }
  } catch {
    return DEFAULT_COL_WIDTHS
  }
}

function HeaderCell({ onResize, children }: { onResize?: (delta: number) => void; children: React.ReactNode }) {
  return (
    <div className="relative flex h-full items-center min-w-0 pr-2">
      {children}
      {onResize ? <ResizeHandle onResize={onResize} /> : null}
    </div>
  )
}

// Drag handle on the right edge of a header cell. Tracks mousemove globally
// until mouseup to keep dragging smooth even when the cursor leaves the handle.
function ResizeHandle({ onResize }: { onResize: (delta: number) => void }) {
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    let lastX = e.clientX
    const onMove = (me: MouseEvent) => {
      const delta = me.clientX - lastX
      lastX = me.clientX
      if (delta !== 0) onResize(delta)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [onResize])

  return (
    <>
      {/* Always-visible column boundary — full-height, slightly darker than bg-border so it reads as interactive */}
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-px bg-muted-foreground/25" />
      {/* Wider hit-target for the drag. On hover the handle thickens to a 2px violet bar. */}
      <div
        onMouseDown={handleMouseDown}
        className="group absolute right-0 top-0 z-20 h-full w-3 cursor-col-resize"
      >
        <div className="absolute right-0 top-0 h-full w-0.5 bg-violet-500 opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </>
  )
}

// Column-header filter: click the label, get a popover with options. Dot indicates active filter.
function FilterPopover({ label, active, children }: { label: string; active: boolean; children: React.ReactNode }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1 text-left uppercase transition-colors ${active ? 'text-foreground' : 'hover:text-foreground'}`}
        >
          {label}
          {active && <span className="size-1.5 rounded-full bg-violet-500" />}
          <ChevronDown className="size-3 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        {children}
      </PopoverContent>
    </Popover>
  )
}

function FilterOption({ selected, disabled, onClick, children }: { selected: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm transition-colors ${
        disabled ? 'cursor-not-allowed opacity-40' : selected ? 'bg-accent font-medium' : 'hover:bg-accent'
      }`}
    >
      {children}
      {selected && <Check className="size-3.5 text-violet-600" />}
    </button>
  )
}

// Filters live in local state for instant UX but mirror to URL search params so the
// view is shareable. Initial values seed from URL on mount; ongoing changes write back
// via fire-and-forget navigate (we don't re-read URL after mount, so writes don't re-render).
type SortKey = 'sla' | 'brief_date' | 'event_created' | 'event_date'
type SortDir = 'asc' | 'desc'
type EventDateFilter = 'upcoming' | 'next-7d' | 'next-30d' | 'past' | 'no-date'

function matchesEventDateFilter(item: QueueItem, filter: EventDateFilter | undefined): boolean {
  if (!filter) return true
  if (!item.event_date) return filter === 'no-date'
  if (filter === 'no-date') return false
  const days = DateTime.fromISO(item.event_date).diffNow('days').days
  // `>= -1` keeps today-of-event in the "upcoming" buckets (Luxon returns small
  // negatives once the day starts).
  switch (filter) {
    case 'upcoming':  return days >= -1
    case 'next-7d':   return days >= -1 && days <= 7
    case 'next-30d':  return days >= -1 && days <= 30
    case 'past':      return days < -1
  }
}

function QueueView({ initialStatus, initialAssignees, initialLocations, initialCompanies, initialEventDate, initialSortKey, initialSortDir }: {
  initialStatus?: string
  initialAssignees?: string
  initialLocations?: string
  initialCompanies?: string
  initialEventDate?: string
  initialSortKey?: string
  initialSortDir?: string
}) {
  const navigate = useNavigate({ from: Route.fullPath })
  const userDetail = useAuthStore((s) => s.userDetail)
  const currentUserId = (userDetail as any)?.id as number | undefined

  const parseList = (s: string | undefined) => s ? s.split('|').filter(Boolean) : []
  const [statusFilter, setStatusFilter] = useState<string | undefined>(initialStatus)
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(parseList(initialAssignees))
  const [selectedLocations, setSelectedLocations] = useState<string[]>(parseList(initialLocations))
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>(parseList(initialCompanies))
  const validEventDate = (v: string | undefined): EventDateFilter | undefined =>
    v === 'upcoming' || v === 'next-7d' || v === 'next-30d' || v === 'past' || v === 'no-date' ? v : undefined
  const [eventDateFilter, setEventDateFilter] = useState<EventDateFilter | undefined>(validEventDate(initialEventDate))
  const validSortKey = (k: string | undefined): SortKey =>
    (k === 'sla' || k === 'brief_date' || k === 'event_created' || k === 'event_date') ? k : 'brief_date'
  const validSortDir = (d: string | undefined): SortDir => d === 'asc' ? 'asc' : 'desc'
  const [sortKey, setSortKey] = useState<SortKey>(validSortKey(initialSortKey))
  const [sortDir, setSortDir] = useState<SortDir>(validSortDir(initialSortDir))

  // Column widths persist to localStorage so admins keep their preferred layout across sessions.
  const [colWidths, setColWidths] = useState<Record<ColKey, number>>(loadStoredWidths)
  // Trailing `minmax(0, 1fr)` is an invisible spacer column that absorbs any leftover
  // horizontal space so the header bg and row borders run to the table's right edge.
  // Without it, when total column widths < container width, you'd see a white gap.
  const gridTemplateColumns = useMemo(
    () => COL_ORDER.map((k) => `${colWidths[k]}px`).join(' ') + ' minmax(0, 1fr)',
    [colWidths],
  )
  const resize = useCallback((colKey: ColKey) => (delta: number) => {
    setColWidths((prev) => {
      const next = { ...prev, [colKey]: Math.max(MIN_COL_WIDTHS[colKey], prev[colKey] + delta) }
      try { window.localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(next)) } catch { /* quota / privacy mode */ }
      return next
    })
  }, [])
  const resetColWidths = useCallback(() => {
    setColWidths(DEFAULT_COL_WIDTHS)
    try { window.localStorage.removeItem(COL_WIDTHS_STORAGE_KEY) } catch { /* noop */ }
  }, [])

  // Centralized URL writer — fire-and-forget, doesn't block UI updates.
  const writeUrl = useCallback((patch: {
    status?: string
    assignees?: string[]
    locations?: string[]
    companies?: string[]
    eventDate?: EventDateFilter | ''
    sortKey?: SortKey
    sortDir?: SortDir
  }) => {
    void navigate({
      search: (prev: any) => ({
        ...prev,
        ...(patch.status !== undefined && { status: patch.status || undefined }),
        ...(patch.assignees !== undefined && { assignees: patch.assignees.length ? patch.assignees.join('|') : undefined }),
        ...(patch.locations !== undefined && { locations: patch.locations.length ? patch.locations.join('|') : undefined }),
        ...(patch.companies !== undefined && { companies: patch.companies.length ? patch.companies.join('|') : undefined }),
        ...(patch.eventDate !== undefined && { eventDate: patch.eventDate || undefined }),
        ...(patch.sortKey !== undefined && { sortKey: patch.sortKey === 'brief_date' ? undefined : patch.sortKey }),
        ...(patch.sortDir !== undefined && { sortDir: patch.sortDir === 'desc' ? undefined : patch.sortDir }),
      }),
      replace: true,
    })
  }, [navigate])

  const updateStatus = useCallback((v: string | undefined) => {
    setStatusFilter(v)
    writeUrl({ status: v ?? '' })
  }, [writeUrl])

  const updateAssignees = useCallback((next: string[]) => {
    setSelectedAssignees(next)
    writeUrl({ assignees: next })
  }, [writeUrl])

  const updateLocations = useCallback((next: string[]) => {
    setSelectedLocations(next)
    writeUrl({ locations: next })
  }, [writeUrl])

  const updateCompanies = useCallback((next: string[]) => {
    setSelectedCompanies(next)
    writeUrl({ companies: next })
  }, [writeUrl])

  const updateEventDate = useCallback((v: EventDateFilter | undefined) => {
    setEventDateFilter(v)
    writeUrl({ eventDate: v ?? '' })
  }, [writeUrl])

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      const next: SortDir = sortDir === 'desc' ? 'asc' : 'desc'
      setSortDir(next)
      writeUrl({ sortKey: key, sortDir: next })
    } else {
      setSortKey(key)
      setSortDir('desc')
      writeUrl({ sortKey: key, sortDir: 'desc' })
    }
  }, [sortKey, sortDir, writeUrl])

  const setSort = useCallback((key: SortKey, dir: SortDir) => {
    setSortKey(key)
    setSortDir(dir)
    writeUrl({ sortKey: key, sortDir: dir })
  }, [writeUrl])

  const clearSort = useCallback(() => {
    setSortKey('brief_date')
    setSortDir('desc')
    writeUrl({ sortKey: 'brief_date', sortDir: 'desc' })
  }, [writeUrl])

  // Sort + all filters live client-side: dataset is bounded (server limit 500), so
  // we re-sort/filter in memory without re-fetching.
  const { data, isPending, isError } = useQuery({
    queryKey: ['admin-recs-queue'],
    queryFn: async () => {
      const res = await apiGet<{ data: QueueItem[] | null }>('queue', {})
      return res.data ?? []
    },
    staleTime: 30_000,
  })

  // Unique cities present in the current dataset, sorted with counts.
  // Backend `location` is a full venue address like "200 Park Ave, New York, NY, USA".
  // We pull `City, State` out so the filter groups multiple addresses in the same city.
  const availableLocations = useMemo(() => {
    const counts = new Map<string, number>()
    for (const i of data ?? []) {
      const city = extractCityState(i.location)
      if (city) counts.set(city, (counts.get(city) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([location, count]) => ({ location, count }))
      .sort((a, b) => a.location.localeCompare(b.location))
  }, [data])

  const toggleLocation = useCallback((loc: string) => {
    const next = selectedLocations.includes(loc) ? selectedLocations.filter((l) => l !== loc) : [...selectedLocations, loc]
    updateLocations(next)
  }, [selectedLocations, updateLocations])

  const toggleCompany = useCallback((c: string) => {
    const next = selectedCompanies.includes(c) ? selectedCompanies.filter((x) => x !== c) : [...selectedCompanies, c]
    updateCompanies(next)
  }, [selectedCompanies, updateCompanies])

  // Unique companies present in the dataset, sorted with counts.
  const availableCompanies = useMemo(() => {
    const counts = new Map<string, number>()
    for (const i of data ?? []) {
      if (i.entity_name) counts.set(i.entity_name, (counts.get(i.entity_name) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [data])

  const toggleAssignee = useCallback((key: string) => {
    const next = selectedAssignees.includes(key) ? selectedAssignees.filter((k) => k !== key) : [...selectedAssignees, key]
    updateAssignees(next)
  }, [selectedAssignees, updateAssignees])

  // Unique assignees present in the dataset + unassigned count. Current user is
  // pinned first (most common self-filter), then the rest alphabetically.
  const availableAssignees = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>()
    let unassignedCount = 0
    for (const i of data ?? []) {
      if (i.revel_recs_admin_assignee_id == null || !i.assignee_name) {
        unassignedCount += 1
        continue
      }
      const key = String(i.revel_recs_admin_assignee_id)
      const existing = counts.get(key)
      if (existing) existing.count += 1
      else counts.set(key, { name: i.assignee_name, count: 1 })
    }
    const entries = Array.from(counts.entries()).map(([key, { name, count }]) => ({ key, name, count }))
    const meKey = currentUserId != null ? String(currentUserId) : null
    const me = meKey ? entries.find((e) => e.key === meKey) : undefined
    const others = entries
      .filter((e) => e.key !== meKey)
      .sort((a, b) => a.name.localeCompare(b.name))
    const named = me ? [me, ...others] : others
    return { named, unassignedCount }
  }, [data, currentUserId])

  // Stats — computed off the unfiltered set so the numbers stay stable as the user filters
  const stats = useMemo(() => {
    const all = data ?? []
    let breached = 0
    let published = 0
    for (const i of all) {
      if (i.revel_recs_status === 'published') published += 1
      else if (i.brief_submitted_at) {
        const hours = DateTime.now().diff(DateTime.fromISO(i.brief_submitted_at), 'hours').hours
        if (hours >= 48) breached += 1
      }
    }
    return { total: all.length, breached, published }
  }, [data])

  // Client-side status + assignee + location filters + sort
  const items = useMemo(() => {
    const all = data ?? []
    let filtered = statusFilter ? all.filter((i) => i.revel_recs_status === statusFilter) : all
    if (selectedAssignees.length > 0) {
      const set = new Set(selectedAssignees)
      filtered = filtered.filter((i) => {
        const key = i.revel_recs_admin_assignee_id == null ? 'unassigned' : String(i.revel_recs_admin_assignee_id)
        return set.has(key)
      })
    }
    if (selectedLocations.length > 0) {
      const set = new Set(selectedLocations)
      filtered = filtered.filter((i) => {
        const city = extractCityState(i.location)
        return city != null && set.has(city)
      })
    }
    if (selectedCompanies.length > 0) {
      const set = new Set(selectedCompanies)
      filtered = filtered.filter((i) => i.entity_name != null && set.has(i.entity_name))
    }
    if (eventDateFilter) {
      filtered = filtered.filter((i) => matchesEventDateFilter(i, eventDateFilter))
    }
    const dirMul = sortDir === 'desc' ? -1 : 1

    // SLA "score" = hours elapsed (pending) OR resolution duration (published).
    // Higher = worse, so desc puts the most breached at the top.
    const slaScore = (i: QueueItem): number => {
      if (!i.brief_submitted_at) return -Infinity
      const end = i.revel_recs_status === 'published' && i.published_at
        ? DateTime.fromISO(i.published_at)
        : DateTime.now()
      return end.diff(DateTime.fromISO(i.brief_submitted_at), 'hours').hours
    }

    const getSortField = (i: QueueItem): string | null => {
      if (sortKey === 'brief_date') return i.brief_submitted_at
      if (sortKey === 'event_date') return i.event_date
      return i.created_at
    }
    return [...filtered].sort((a, b) => {
      if (sortKey === 'sla') {
        return dirMul * (slaScore(a) - slaScore(b))
      }
      const av = getSortField(a)
      const bv = getSortField(b)
      if (!av && !bv) return 0
      if (!av) return 1
      if (!bv) return -1
      return dirMul * av.localeCompare(bv)
    })
  }, [data, statusFilter, selectedAssignees, selectedLocations, selectedCompanies, eventDateFilter, sortKey, sortDir])

  const hasActiveFilters = !!statusFilter || selectedAssignees.length > 0 || selectedLocations.length > 0 || selectedCompanies.length > 0 || !!eventDateFilter
  const clearAllFilters = useCallback(() => {
    setStatusFilter(undefined)
    setSelectedAssignees([])
    setSelectedLocations([])
    setSelectedCompanies([])
    setEventDateFilter(undefined)
    writeUrl({ status: '', assignees: [], locations: [], companies: [], eventDate: '' })
  }, [writeUrl])

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown className="ml-0.5 inline size-3 opacity-30" />
    return sortDir === 'desc'
      ? <ChevronDown className="ml-0.5 inline size-3" />
      : <ChevronUp className="ml-0.5 inline size-3" />
  }

  return (
    <div className="min-h-screen bg-muted/20">
      {/* Header — title + mini-stats. Filters live in the column headers below. */}
      <div className="border-b bg-card py-4">
        <div className="mx-auto max-w-[1400px] px-6">
          <div className="flex items-center gap-3">
            <div className="flex size-9 items-center justify-center rounded-lg bg-violet-100">
              <Sparkles className="size-4 text-violet-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">Revel Recs</h1>
              <p className="text-xs text-muted-foreground">
                {stats.total} brief{stats.total !== 1 ? 's' : ''}
                {stats.breached > 0 && (
                  <> · <span className="text-red-600 font-medium">{stats.breached} breached</span></>
                )}
                {stats.published > 0 && (
                  <> · <span className="text-emerald-600 font-medium">{stats.published} published</span></>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mx-auto max-w-[1400px] px-6 py-4">
        {isPending ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-lg border bg-card p-4">
                <div className="flex items-center gap-4">
                  <div className="h-5 w-48 rounded bg-muted" />
                  <div className="h-4 w-32 rounded bg-muted" />
                  <div className="h-4 w-24 rounded bg-muted" />
                  <div className="flex-1" />
                  <div className="h-6 w-20 rounded-full bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 py-10 text-center">
            <AlertTriangle className="mx-auto size-8 text-destructive/60" />
            <p className="mt-2 text-sm text-destructive">Failed to load queue. Please refresh.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
          <div className="overflow-x-auto">
            {/* 9-column table header — spec order */}
            <div
              className="grid w-fit min-w-full items-stretch gap-2 border-b bg-muted/30 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              style={{ gridTemplateColumns }}
            >
              <HeaderCell onResize={resize('event')}>
              <FilterPopover label="Event" active={!!eventDateFilter || sortKey === 'event_date'}>
                <div>
                  <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Filter by date</p>
                  <div className="space-y-0.5">
                    <FilterOption selected={!eventDateFilter} onClick={() => updateEventDate(undefined)}>All</FilterOption>
                    <FilterOption selected={eventDateFilter === 'upcoming'} onClick={() => updateEventDate('upcoming')}>Upcoming</FilterOption>
                    <FilterOption selected={eventDateFilter === 'next-7d'} onClick={() => updateEventDate('next-7d')}>Next 7 days</FilterOption>
                    <FilterOption selected={eventDateFilter === 'next-30d'} onClick={() => updateEventDate('next-30d')}>Next 30 days</FilterOption>
                    <FilterOption selected={eventDateFilter === 'past'} onClick={() => updateEventDate('past')}>Past events</FilterOption>
                    <FilterOption selected={eventDateFilter === 'no-date'} onClick={() => updateEventDate('no-date')}>No event date</FilterOption>
                  </div>
                </div>
                <div className="mt-3 border-t pt-2">
                  <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Sort</p>
                  <div className="space-y-0.5">
                    <FilterOption
                      selected={sortKey === 'event_date' && sortDir === 'desc'}
                      onClick={() => setSort('event_date', 'desc')}
                    >
                      Newest first
                    </FilterOption>
                    <FilterOption
                      selected={sortKey === 'event_date' && sortDir === 'asc'}
                      onClick={() => setSort('event_date', 'asc')}
                    >
                      Oldest first
                    </FilterOption>
                    {sortKey === 'event_date' && (
                      <button
                        type="button"
                        onClick={clearSort}
                        className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                      >
                        Clear sort
                      </button>
                    )}
                  </div>
                </div>
              </FilterPopover>
              </HeaderCell>
              <HeaderCell onResize={resize('company')}>
              <FilterPopover label="Company" active={selectedCompanies.length > 0}>
                {availableCompanies.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-muted-foreground">No companies available.</p>
                ) : (
                  <>
                    <div className="max-h-64 space-y-0.5 overflow-y-auto">
                      {availableCompanies.map(({ name, count }) => (
                        <FilterOption
                          key={name}
                          selected={selectedCompanies.includes(name)}
                          onClick={() => toggleCompany(name)}
                        >
                          <span className="normal-case">
                            {name} <span className="ml-1 text-xs text-muted-foreground">({count})</span>
                          </span>
                        </FilterOption>
                      ))}
                    </div>
                    {selectedCompanies.length > 0 && (
                      <button
                        type="button"
                        onClick={() => updateCompanies([])}
                        className="mt-2 w-full rounded border-t px-2 pt-2 text-left text-xs text-muted-foreground hover:text-foreground"
                      >
                        Clear ({selectedCompanies.length})
                      </button>
                    )}
                  </>
                )}
              </FilterPopover>
              </HeaderCell>
              <HeaderCell onResize={resize('owner')}>
                <span>Owner</span>
              </HeaderCell>
              <HeaderCell onResize={resize('location')}>
              <FilterPopover
                label="Location"
                active={selectedLocations.length > 0}
              >
                {availableLocations.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-muted-foreground">No locations available.</p>
                ) : (
                  <>
                    <div className="max-h-64 space-y-0.5 overflow-y-auto">
                      {availableLocations.map(({ location, count }) => (
                        <FilterOption
                          key={location}
                          selected={selectedLocations.includes(location)}
                          onClick={() => toggleLocation(location)}
                        >
                          <span className="normal-case">
                            {location} <span className="ml-1 text-xs text-muted-foreground">({count})</span>
                          </span>
                        </FilterOption>
                      ))}
                    </div>
                    {selectedLocations.length > 0 && (
                      <button
                        type="button"
                        onClick={() => updateLocations([])}
                        className="mt-2 w-full rounded border-t px-2 pt-2 text-left text-xs text-muted-foreground hover:text-foreground"
                      >
                        Clear ({selectedLocations.length})
                      </button>
                    )}
                  </>
                )}
              </FilterPopover>
              </HeaderCell>
              <HeaderCell onResize={resize('created')}>
                <button
                  className="flex items-center text-left uppercase hover:text-foreground transition-colors"
                  onClick={() => toggleSort('event_created')}
                >
                  Created
                  <SortIcon col="event_created" />
                </button>
              </HeaderCell>
              <HeaderCell onResize={resize('submitted')}>
                <button
                  className="flex items-center text-left uppercase hover:text-foreground transition-colors"
                  onClick={() => toggleSort('brief_date')}
                >
                  Submitted
                  <SortIcon col="brief_date" />
                </button>
              </HeaderCell>
              <HeaderCell onResize={resize('assigned')}>
              <FilterPopover label="Assigned" active={selectedAssignees.length > 0}>
                {availableAssignees.named.length === 0 && availableAssignees.unassignedCount === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-muted-foreground">No assignments yet.</p>
                ) : (
                  <>
                    <div className="max-h-64 space-y-0.5 overflow-y-auto">
                      {availableAssignees.unassignedCount > 0 && (
                        <FilterOption
                          selected={selectedAssignees.includes('unassigned')}
                          onClick={() => toggleAssignee('unassigned')}
                        >
                          <span className="italic text-muted-foreground">
                            Unassigned <span className="ml-1 text-xs not-italic">({availableAssignees.unassignedCount})</span>
                          </span>
                        </FilterOption>
                      )}
                      {availableAssignees.named.map(({ key, name, count }) => (
                        <FilterOption
                          key={key}
                          selected={selectedAssignees.includes(key)}
                          onClick={() => toggleAssignee(key)}
                        >
                          <span className="normal-case">
                            {name}
                            <span className="ml-1 text-xs text-muted-foreground">({count})</span>
                          </span>
                        </FilterOption>
                      ))}
                    </div>
                    {selectedAssignees.length > 0 && (
                      <button
                        type="button"
                        onClick={() => updateAssignees([])}
                        className="mt-2 w-full rounded border-t px-2 pt-2 text-left text-xs text-muted-foreground hover:text-foreground"
                      >
                        Clear ({selectedAssignees.length})
                      </button>
                    )}
                  </>
                )}
              </FilterPopover>
              </HeaderCell>
              <HeaderCell onResize={resize('status')}>
              <FilterPopover label="Status" active={!!statusFilter}>
                <div className="space-y-0.5">
                  <FilterOption selected={!statusFilter} onClick={() => updateStatus(undefined)}>
                    All
                  </FilterOption>
                  <FilterOption selected={statusFilter === 'brief_submitted'} onClick={() => updateStatus('brief_submitted')}>
                    Brief Submitted
                  </FilterOption>
                  <FilterOption selected={statusFilter === 'in_progress'} onClick={() => updateStatus('in_progress')}>
                    In Progress
                  </FilterOption>
                  <FilterOption selected={statusFilter === 'published'} onClick={() => updateStatus('published')}>
                    Published
                  </FilterOption>
                </div>
              </FilterPopover>
              </HeaderCell>
              <HeaderCell>
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  className="flex items-center text-left uppercase hover:text-foreground transition-colors"
                  onClick={() => toggleSort('sla')}
                >
                  SLA
                  <SortIcon col="sla" />
                </button>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="inline-flex" aria-label="SLA format">
                        <Info className="size-3 text-muted-foreground/60 hover:text-muted-foreground" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[280px] normal-case font-normal">
                      <p className="font-semibold text-foreground">Time to publish recs</p>
                      <p className="mt-1 text-xs">
                        We aim to publish recs within 48 hours of brief submission.
                      </p>
                      <div className="mt-2 space-y-1 text-xs">
                        <p className="flex items-center gap-1.5">
                          <span className="inline-block size-2 rounded-full bg-muted-foreground/40" />
                          <span>Under 24h, on track</span>
                        </p>
                        <p className="flex items-center gap-1.5">
                          <span className="inline-block size-2 rounded-full bg-amber-500" />
                          <span>24–48h, approaching deadline</span>
                        </p>
                        <p className="flex items-center gap-1.5">
                          <span className="inline-block size-2 rounded-full bg-red-500" />
                          <span>Over 48h, past deadline</span>
                        </p>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Once published, shows recs delivered and total time taken.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
              </HeaderCell>
            </div>
            {/* Empty state — kept INSIDE the table so the column-header filters remain reachable */}
            {items.length === 0 ? (
              <div className="py-16 text-center">
                <Sparkles className="mx-auto size-10 text-muted-foreground/40" />
                <p className="mt-3 text-sm text-muted-foreground">
                  {hasActiveFilters ? 'No briefs match the current filters.' : 'No pending briefs.'}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="mt-3 rounded-lg border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : null}
            {/* Rows */}
            {items.map((item) => {
              const sc = getStatusConfig(item.revel_recs_status)
              return (
                <button
                  key={item.id}
                  onClick={() => void navigate({ search: (prev: any) => ({ ...prev, eventId: String(item.id), tab: undefined }) })}
                  className="grid w-fit min-w-full items-center gap-2 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-accent/40"
                  style={{ gridTemplateColumns }}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{item.name || 'Untitled Event'}</p>
                    {(() => {
                      // Always render the second line so rows stay the same height regardless
                      // of whether event_date exists (prevents layout shift when filters change).
                      if (!item.event_date) {
                        return <p className="mt-0.5 text-[11px] text-muted-foreground/40">No event date</p>
                      }
                      const dt = DateTime.fromISO(item.event_date)
                      const days = dt.diffNow('days').days
                      // 3 tiers: past events recede (can't act on them), upcoming-within-7d pop red, rest muted.
                      // `>= -1` for "soon" catches same-day reads (Luxon returns small negative once the day starts).
                      let cls: string
                      if (days < -1) cls = 'text-muted-foreground/50'
                      else if (days <= 7) cls = 'text-red-600'
                      else cls = 'text-muted-foreground'
                      return (
                        <p className={`mt-0.5 text-[11px] ${cls}`}>
                          {dt.toRelative()} · {dt.toFormat('LLL d, yyyy')}
                        </p>
                      )
                    })()}
                  </div>
                  <span className="truncate text-xs text-muted-foreground">{item.entity_name || '—'}</span>
                  <span className="truncate text-xs text-muted-foreground">{item.creator_name || '—'}</span>
                  <span className="truncate text-xs text-muted-foreground">{item.location || '—'}</span>
                  <span className="text-xs text-muted-foreground">
                    {item.created_at
                      ? DateTime.fromISO(item.created_at).toFormat('LLL d, yyyy')
                      : '—'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {item.brief_submitted_at
                      ? DateTime.fromISO(item.brief_submitted_at).toFormat('LLL d, yyyy')
                      : '—'}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">{item.assignee_name ?? ''}</span>
                  <span>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${sc.bg} ${sc.text}`}>
                      {sc.label}
                    </span>
                  </span>
                  {(() => {
                    if (!item.brief_submitted_at) {
                      return <span className="text-xs text-muted-foreground">—</span>
                    }
                    if (item.revel_recs_status === 'published' && item.published_at) {
                      const { text, withinSla } = formatResolution(item.brief_submitted_at, item.published_at)
                      return (
                        <span className="text-xs text-muted-foreground">
                          <span className={withinSla ? 'text-emerald-600' : 'text-red-600'}>{text}</span>
                          {' · '}
                          {item.rec_count} rec{item.rec_count === 1 ? '' : 's'}
                        </span>
                      )
                    }
                    return (
                      <span className={`text-xs ${SLA_TIER_CLASS[getSlaTier(item.brief_submitted_at, item.revel_recs_status)]}`}>
                        {formatSlaElapsed(item.brief_submitted_at, item.revel_recs_status, item.rec_count)}
                      </span>
                    )
                  })()}
                </button>
              )
            })}
          </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// EVENT DETAIL VIEW
// ═══════════════════════════════════════════════════════════════════════════════

function EventDetailView({ eventId, tab }: { eventId: string; tab: string }) {
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()

  // Fetch event detail
  const { data: eventData, isLoading: eventLoading } = useQuery({
    queryKey: ['admin-recs-event', eventId],
    queryFn: async () => {
      const res = await apiGet<{ data: EventDetailData | null }>('eventDetail', { eventId })
      return res.data
    },
    staleTime: 60_000,
  })

  // Fetch recs from DB via API
  const { data: recsData, isLoading: recsLoading } = useQuery({
    queryKey: ['admin-recs-list', eventId],
    queryFn: async () => {
      const res = await apiGet<{ data: RevelRec[] | null }>('eventRecs', { eventId })
      return res.data ?? []
    },
    staleTime: 30_000,
  })

  // Fetch client shortlist
  const { data: shortlistData } = useQuery({
    queryKey: ['admin-recs-shortlist', eventId],
    queryFn: async () => {
      const res = await apiGet<{ data: ShortlistItem[] | null }>('clientShortlist', { eventId })
      return res.data ?? []
    },
    staleTime: 60_000,
  })

  // Resolve space info for recs. Track which IDs we've already requested via a ref
  // so the effect only depends on the stable query result, not the cache state.
  const [spaceCache, setSpaceCache] = useState<Map<number, ResolvedSpace>>(new Map())
  const requestedSpaceIdsRef = useRef<Set<number>>(new Set())
  const recs = recsData ?? []

  // Resolve space info for client shortlist
  const [shortlistSpaces, setShortlistSpaces] = useState<Map<number, { name: string; venueName: string; city: string }>>(new Map())
  const requestedShortlistIdsRef = useRef<Set<number>>(new Set())
  const shortlist = shortlistData ?? []

  // EventDetailView is reused across event navigation — clear the request-tracking
  // refs so a previous event's failed-resolve IDs don't lock the new event out.
  useEffect(() => {
    requestedSpaceIdsRef.current = new Set()
    requestedShortlistIdsRef.current = new Set()
  }, [eventId])

  useEffect(() => {
    if (!recsData) return
    const missing: number[] = []
    for (const r of recsData) {
      if (!requestedSpaceIdsRef.current.has(r.space_id)) {
        requestedSpaceIdsRef.current.add(r.space_id)
        missing.push(r.space_id)
      }
    }
    if (missing.length === 0) return
    void resolveSpacesByIds(missing)
      .then((resolved) => {
        setSpaceCache((prev) => {
          const next = new Map(prev)
          for (const [k, v] of resolved) next.set(k, v)
          return next
        })
      })
      .catch(() => {
        // Free the failed IDs so a later render can retry.
        for (const id of missing) requestedSpaceIdsRef.current.delete(id)
      })
  }, [recsData])

  useEffect(() => {
    if (!shortlistData) return
    const missing: number[] = []
    for (const s of shortlistData) {
      const id = s.space_id
      if (typeof id === 'number' && !requestedShortlistIdsRef.current.has(id)) {
        requestedShortlistIdsRef.current.add(id)
        missing.push(id)
      }
    }
    if (missing.length === 0) return
    void resolveShortlistSpaces(missing)
      .then((resolved) => {
        setShortlistSpaces((prev) => {
          const next = new Map(prev)
          for (const [k, v] of resolved) next.set(k, v)
          return next
        })
      })
      .catch(() => {
        for (const id of missing) requestedShortlistIdsRef.current.delete(id)
      })
  }, [shortlistData])

  // Reference venues are enriched server-side via Supabase (see adminRecsV2.ts).
  // We deliberately don't go through Typesense here because client-added ghost
  // venues aren't published, and the public Typesense key filters those out.
  const venueReferences = eventData?.venue_references ?? []

  const pendingRecs = useMemo(() => recs.filter((r) => r.status === 'pending'), [recs])
  const activeRecs = useMemo(() => recs.filter((r) => r.status === 'active'), [recs])
  const shortlistedRecs = useMemo(() => recs.filter((r) => r.status === 'shortlisted'), [recs])
  const downvotedRecs = useMemo(() => recs.filter((r) => r.status === 'downvoted'), [recs])
  const allLiveRecs = useMemo(() => recs.filter((r) => r.status === 'active' || r.status === 'pending'), [recs])
  const allLiveSpaceIds = useMemo(() => new Set(allLiveRecs.map((r) => r.space_id)), [allLiveRecs])
  const downvotedSpaceIds = useMemo(() => new Set(downvotedRecs.map((r) => r.space_id)), [downvotedRecs])
  const shortlistSpaceIds = useMemo(
    () => new Set(shortlist.map((s) => s.space_id).filter((id): id is number => typeof id === 'number')),
    [shortlist],
  )

  // Use persisted status from DB (parsed through the shared helper for safety)
  const currentStatus: RevelRecsAdminStatus = parseRevelRecsAdminStatus(eventData?.revel_recs_status)

  // Rec mutations via API
  const addRecMut = useMutation({
    mutationFn: async (params: { spaceId: number; conciergeNote?: string }) => {
      return apiPost({ action: 'addRec', eventId: parseInt(eventId, 10), spaceId: params.spaceId, conciergeNote: params.conciergeNote })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-recs-list', eventId] })
      queryClient.invalidateQueries({ queryKey: ['admin-recs-queue'] })
      toast.success('Recommendation added')
    },
  })

  const removeRecMut = useMutation({
    mutationFn: async (recId: number) => {
      return apiPost({ action: 'removeRec', recId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-recs-list', eventId] })
      queryClient.invalidateQueries({ queryKey: ['admin-recs-queue'] })
      toast.success('Recommendation removed')
    },
  })

  const updateNoteMut = useMutation({
    mutationFn: async (params: { recId: number; conciergeNote: string }) => {
      return apiPost({ action: 'updateRecNote', recId: params.recId, conciergeNote: params.conciergeNote })
    },
  })

  const publishRecsMut = useMutation({
    mutationFn: async () => {
      return apiPost({ action: 'publishRecs', eventId: parseInt(eventId, 10) })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-recs-list', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-recs-queue'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-recs-event', eventId] })
      toast.success('Recommendations published — now visible to client')
    },
  })

  // Staff users for Assigned To dropdown (stale 5 min — ops staff list rarely changes)
  const { data: staffUsersData, isError: staffUsersError } = useQuery({
    queryKey: ['admin-staff-users'],
    queryFn: async () => {
      const res = await apiGet<{ data: StaffUser[] | null }>('staffUsers')
      return res.data ?? []
    },
    staleTime: 5 * 60_000,
  })

  // Admin field mutation: optimistic update on event detail, invalidate on settled
  const updateAdminFieldsMut = useMutation({
    mutationFn: async (fields: { revel_recs_admin_assignee_id?: number | null; revel_recs_status?: RevelRecsAdminStatus }) => {
      return apiPost({ action: 'updateEventAdminFields', eventId: parseInt(eventId, 10), ...fields })
    },
    onMutate: async (fields) => {
      await queryClient.cancelQueries({ queryKey: ['admin-recs-event', eventId] })
      const prev = queryClient.getQueryData(['admin-recs-event', eventId])
      queryClient.setQueryData(['admin-recs-event', eventId], (old: any) => old ? { ...old, ...fields } : old)
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev !== undefined) queryClient.setQueryData(['admin-recs-event', eventId], ctx.prev)
      toast.error('Failed to save admin field')
    },
    onSuccess: (_data, vars) => {
      const eid = parseInt(eventId, 10)
      // Fire only on confirmed write so analytics funnels aren't skewed by failed mutations.
      if ('revel_recs_status' in vars && vars.revel_recs_status !== undefined) {
        posthog?.capture?.(PostHogEvent.ADMIN_RECS_STATUS_CHANGED, { event_id: eid, status: vars.revel_recs_status })
      }
      if ('revel_recs_admin_assignee_id' in vars) {
        posthog?.capture?.(PostHogEvent.ADMIN_RECS_ASSIGNEE_CHANGED, { event_id: eid, assignee_id: vars.revel_recs_admin_assignee_id ?? null })
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-recs-event', eventId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-recs-queue'] })
    },
  })

  // Drag-reorder mutation for pending recs
  const reorderRecsMut = useMutation({
    mutationFn: async (orderedIds: number[]) => {
      return apiPost({ action: 'reorderRecs', eventId: parseInt(eventId, 10), recIds: orderedIds })
    },
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: ['admin-recs-list', eventId] })
      const prev = queryClient.getQueryData(['admin-recs-list', eventId])
      queryClient.setQueryData(['admin-recs-list', eventId], (old: RevelRec[] | undefined) => {
        if (!old) return old
        const pendingById = new Map(old.filter((r) => r.status === 'pending').map((r) => [r.id, r]))
        const reorderedPending = orderedIds.map((id, i) => ({ ...pendingById.get(id)!, sort_order: i }))
        const others = old.filter((r) => r.status !== 'pending')
        return [...reorderedPending, ...others]
      })
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev !== undefined) queryClient.setQueryData(['admin-recs-list', eventId], ctx.prev)
      toast.error('Failed to save new order')
    },
    onSuccess: (_data, orderedIds) => {
      posthog?.capture?.(PostHogEvent.ADMIN_RECS_REORDERED, {
        event_id: parseInt(eventId, 10),
        rec_count: orderedIds.length,
      })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-recs-list', eventId] })
    },
  })

  const event = eventData
  const firstDate = event?.dates?.[0]

  return (
    <div className="flex w-full h-full justify-start flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b bg-card px-6 py-3">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate({ search: (prev: any) => ({ ...prev, eventId: undefined, tab: undefined }) })}
              className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="size-4" />
              Queue
            </button>

            <div className="h-5 w-px bg-border" />

            {eventLoading ? (
              <div className="h-5 w-48 animate-pulse rounded bg-muted" />
            ) : (
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <h1 className="text-base font-bold truncate">{event?.name || `Event #${eventId}`}</h1>
                {event?.entity?.name && (
                  <Badge variant="outline" className="text-[11px]">{event.entity.name}</Badge>
                )}
                <StatusPill status={currentStatus} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={tab}
        onValueChange={(t) => navigate({ search: (prev: any) => ({ ...prev, tab: t }), replace: true })}
        className="flex flex-1 flex-col overflow-hidden"
      >
        <div className="flex-shrink-0 border-b bg-card px-6">
          <div className="mx-auto max-w-7xl">
            <TabsList className="h-11 rounded-none border-b-0 bg-transparent p-0">
              <TabsTrigger value="brief" className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-600 data-[state=active]:text-violet-600 data-[state=active]:shadow-none">
                Brief
              </TabsTrigger>
              <TabsTrigger value="recommendations" className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-600 data-[state=active]:text-violet-600 data-[state=active]:shadow-none">
                Recommendations
                {allLiveRecs.length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                    {allLiveRecs.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="search" className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-600 data-[state=active]:text-violet-600 data-[state=active]:shadow-none">
                Search Venues
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <TabsContent value="brief" className="mt-0 h-full">
            <BriefTab
              event={event}
              firstDate={firstDate}
              shortlist={shortlist}
              shortlistSpaces={shortlistSpaces}
              venueReferences={venueReferences}
              loading={eventLoading}
              activeRecSpaceIds={allLiveSpaceIds}
              onAddRec={(spaceId) => addRecMut.mutate({ spaceId })}
              isAddingRec={addRecMut.isPending}
              staffUsers={staffUsersData ?? []}
              staffUsersError={staffUsersError}
              currentAssigneeId={eventData?.revel_recs_admin_assignee_id ?? null}
              currentStatus={currentStatus}
              onUpdateAdminField={(fields) => updateAdminFieldsMut.mutate(fields)}
              isUpdatingAdminField={updateAdminFieldsMut.isPending}
            />
          </TabsContent>
          <TabsContent value="recommendations" className="mt-0 h-full">
            <RecommendationsTab
              pendingRecs={pendingRecs}
              activeRecs={activeRecs}
              shortlistedRecs={shortlistedRecs}
              downvotedRecs={downvotedRecs}
              spaceCache={spaceCache}
              currentStatus={currentStatus}
              onRemoveRec={(id) => removeRecMut.mutate(id)}
              onUpdateNote={(recId, note) => updateNoteMut.mutate({ recId, conciergeNote: note })}
              onPublish={() => publishRecsMut.mutate()}
              isPublishing={publishRecsMut.isPending}
              shortlist={shortlist}
              shortlistSpaces={shortlistSpaces}
              onReorderPending={(orderedIds) => reorderRecsMut.mutate(orderedIds)}
              isReordering={reorderRecsMut.isPending}
            />
          </TabsContent>
          <TabsContent value="search" className="mt-0 h-full">
            <SearchTab
              event={event}
              activeRecSpaceIds={allLiveSpaceIds}
              downvotedSpaceIds={downvotedSpaceIds}
              shortlistSpaceIds={shortlistSpaceIds}
              onAddRec={(spaceId) => addRecMut.mutate({ spaceId })}
              isAddingRec={addRecMut.isPending}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}

// ── Status pill ──────────────────────────────────────────────────────────────

function StatusPill({ status }: { status: RevelRecsAdminStatus }) {
  const sc = getStatusConfig(status)
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${sc.bg} ${sc.text}`}>
      {sc.label}
    </span>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1: BRIEF
// ═══════════════════════════════════════════════════════════════════════════════

function BriefTab({ event, firstDate, shortlist, shortlistSpaces, venueReferences, loading, activeRecSpaceIds, onAddRec, isAddingRec, staffUsers, staffUsersError, currentAssigneeId, currentStatus, onUpdateAdminField, isUpdatingAdminField }: {
  event: EventDetailData | null | undefined
  firstDate: EventDateRow | undefined
  shortlist: ShortlistItem[]
  shortlistSpaces: Map<number, { name: string; venueName: string; city: string }>
  venueReferences: EventVenueReferenceRow[]
  loading: boolean
  activeRecSpaceIds: Set<number>
  onAddRec: (spaceId: number) => void
  isAddingRec: boolean
  staffUsers: StaffUser[]
  staffUsersError: boolean
  currentAssigneeId: number | null
  currentStatus: RevelRecsAdminStatus
  onUpdateAdminField: (fields: { revel_recs_admin_assignee_id?: number | null; revel_recs_status?: RevelRecsAdminStatus }) => void
  isUpdatingAdminField: boolean
}) {
  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="animate-pulse rounded-xl border bg-card p-6">
            <div className="h-5 w-40 rounded bg-muted" />
            <div className="mt-4 space-y-3">
              <div className="h-4 w-3/4 rounded bg-muted" />
              <div className="h-4 w-1/2 rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!event) return null

  const spaceTypes = Array.isArray(event.space_type) ? event.space_type : []
  const amenities = Array.isArray(event.amenities_requirements) ? event.amenities_requirements : []
  const mediaReqs = Array.isArray(event.media_requirements) ? event.media_requirements : []

  // Reference venues are enriched server-side from main_venue (see adminRecsV2.ts).
  const refVenues = venueReferences.map((ref) => {
    const v = ref.venue
    return {
      venueId: ref.venue_id,
      name: v?.name || `Venue #${ref.venue_id}`,
      city: v?.city || '',
      address: v?.display_address || null,
      imageUrl: v?.image_upload ? buildImageUrl(v.image_upload, { w: 120, h: 120 }) : null,
      rating: typeof v?.rating_google === 'number' ? v.rating_google : null,
      cuisine: null as string | null,
      website: v?.website || null,
      firstSpaceId: v?.first_space_id ?? null,
      isGhost: v?.create_source === 'customer_ghost',
    }
  })

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      {/* Admin section — Assigned To + Status dropdowns. Pinned to the top so the
          ops actions (who owns this, what state is it in) are the first thing seen. */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="border-b bg-muted/20 px-5 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Shield className="size-4 text-muted-foreground" />
            Admin
          </h2>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            {/* Assigned To */}
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Assigned To</dt>
              <dd>
                {staffUsersError ? (
                  <p className="text-xs text-destructive">Failed to load staff users — refresh.</p>
                ) : (
                  <Select
                    value={currentAssigneeId != null ? String(currentAssigneeId) : '__unassigned__'}
                    onValueChange={(val) => {
                      const id = val === '__unassigned__' ? null : parseInt(val, 10)
                      onUpdateAdminField({ revel_recs_admin_assignee_id: id })
                    }}
                    disabled={isUpdatingAdminField}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unassigned__">Unassigned</SelectItem>
                      {staffUsers.map((u) => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {staffUserDisplayName(u)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </dd>
            </div>
            {/* Status */}
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Status</dt>
              <dd>
                <Select
                  value={currentStatus}
                  onValueChange={(val) => {
                    onUpdateAdminField({ revel_recs_status: val as RevelRecsAdminStatus })
                  }}
                  disabled={isUpdatingAdminField}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REVEL_RECS_ADMIN_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {STATUS_CONFIG[s].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </dd>
            </div>
          </div>
          {isUpdatingAdminField && (
            <p className="mt-2 text-[11px] text-muted-foreground">Saving...</p>
          )}
        </div>
      </div>

      {/* Event details */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="border-b bg-muted/20 px-5 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <CalendarDays className="size-4 text-muted-foreground" />
            Event Details
          </h2>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <DetailRow label="Event name" value={event.name} />
            <DetailRow
              label="Event date"
              value={firstDate?.start_date ? formatUtcDateLongFromUnknown(firstDate.start_date) : null}
            />
            <DetailRow
              label="Start time"
              value={
                (event.display_json && typeof event.display_json === 'object' && 'start_time' in event.display_json
                  ? String(event.display_json.start_time)
                  : null) || firstDate?.start_time || null
              }
            />
            <DetailRow
              label="End time"
              value={
                (event.display_json && typeof event.display_json === 'object' && 'end_time' in event.display_json
                  ? String(event.display_json.end_time)
                  : null) || firstDate?.end_time || null
              }
            />
            <DetailRow
              label="Location"
              value={event.location}
              icon={<MapPin className="size-3.5" />}
            />
            <DetailRow
              label="Guest count"
              value={event.num_attendees ? `${event.num_attendees} guests` : null}
              icon={<Users className="size-3.5" />}
            />
            <DetailRow label="Type of Event" value={event.event_type || event.event_format} icon={<Layout className="size-3.5" />} />
            <DetailRow label="Space type" value={spaceTypes.length > 0 ? spaceTypes.join(', ') : null} />
            <DetailRow label="Privacy" value={event.privacy_requirement} icon={<Shield className="size-3.5" />} />
            <DetailRow label="A/V needed" value={mediaReqs.length > 0 ? mediaReqs.join(', ') : null} icon={<Monitor className="size-3.5" />} />
            <DetailRow label="Amenities" value={amenities.length > 0 ? amenities.join(', ') : null} />
            <DetailRow
              label="Budget"
              value={
                event.budget_total ? `$${event.budget_total.toLocaleString()} total` :
                event.budget_per_person ? `$${event.budget_per_person}/person` : null
              }
              icon={<DollarSign className="size-3.5" />}
            />
          </div>

          {event.revel_recs_brief && (
            <div className="mt-5 rounded-lg bg-muted/30 p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="size-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Anything else</span>
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed">{event.revel_recs_brief}</p>
            </div>
          )}

          {event.revel_recs_brief_tags && Array.isArray(event.revel_recs_brief_tags) && event.revel_recs_brief_tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {event.revel_recs_brief_tags.map((tag: string) => (
                <span key={tag} className="inline-flex items-center rounded-full bg-violet-100 px-3 py-1 text-xs font-medium text-violet-700">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Creator info */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="border-b bg-muted/20 px-5 py-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Users className="size-4 text-muted-foreground" />
            Creator
          </h2>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
            <DetailRow label="Full name" value={event.creator?.name || event.creator?.first_name} />
            <DetailRow label="Email" value={event.creator?.email} icon={<Mail className="size-3.5" />} />
            <DetailRow label="Company" value={event.entity?.name} icon={<Building2 className="size-3.5" />} />
            <DetailRow
              label="Event created"
              value={event.created_at ? new Date(event.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null}
              icon={<Clock className="size-3.5" />}
            />
            <DetailRow
              label="Brief submitted"
              value={event.revel_recs_brief_submitted_at ? new Date(event.revel_recs_brief_submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null}
              icon={<Clock className="size-3.5" />}
            />
          </div>
        </div>
      </div>

      {/* Reference venues */}
      {refVenues.length > 0 && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="border-b bg-muted/20 px-5 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Star className="size-4 text-muted-foreground" />
              Reference Venues
              <span className="text-[11px] font-normal text-muted-foreground">({refVenues.length})</span>
            </h2>
          </div>
          <div className="p-5 space-y-3">
            {refVenues.map((v, i) => {
              const spaceId = v.firstSpaceId
              const alreadyAdded = spaceId ? activeRecSpaceIds.has(spaceId) : false

              return (
                <div key={i} className="group flex items-center gap-4 rounded-lg border p-3 transition-colors hover:bg-accent/20">
                  {/* Image */}
                  <div className="size-14 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {v.imageUrl ? (
                      <img src={v.imageUrl} alt="" className="size-full object-cover" />
                    ) : (
                      <MapPin className="size-5 text-muted-foreground" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{v.name}</p>
                      {v.isGhost && (
                        <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700">Ghost</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {v.city && <span className="text-xs text-muted-foreground">{v.city}</span>}
                      {v.rating != null && (
                        <span className="text-xs text-muted-foreground">★ {v.rating.toFixed(1)}</span>
                      )}
                      {v.cuisine && (
                        <span className="text-xs text-muted-foreground">· {v.cuisine}</span>
                      )}
                    </div>
                    {v.address && <p className="text-[11px] text-muted-foreground/70 mt-0.5 truncate">{v.address}</p>}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {v.website && (
                      <a
                        href={v.website.startsWith('http') ? v.website : `https://${v.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Globe className="size-3" /> Web
                      </a>
                    )}
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v.name} ${v.city}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <MapPin className="size-3" /> Maps
                    </a>
                    {spaceId && !alreadyAdded && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800 text-[11px]"
                        onClick={() => onAddRec(spaceId)}
                        disabled={isAddingRec}
                      >
                        <Plus className="size-3" /> Add as Rec
                      </Button>
                    )}
                    {spaceId && alreadyAdded && (
                      <span className="flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700">
                        <Check className="size-3" /> Added
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Client shortlist */}
      {shortlist.length > 0 && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="border-b bg-muted/20 px-5 py-3">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Bookmark className="size-4 text-muted-foreground" />
              Client Shortlist
            </h2>
          </div>
          <div className="p-5 space-y-2">
            {shortlist.map((item) => {
              const space = item.space_id != null ? shortlistSpaces.get(item.space_id) : undefined
              return (
                <div key={item.id} className="flex items-center gap-3 rounded-lg border p-3">
                  <div className="flex size-9 items-center justify-center rounded-lg bg-blue-50 flex-shrink-0">
                    <Bookmark className="size-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{space?.venueName || `Space #${item.space_id}`}</p>
                    {space?.city && <p className="text-xs text-muted-foreground">{space.city}</p>}
                  </div>
                  {item.created_at && (
                    <span className="text-[11px] text-muted-foreground flex-shrink-0">
                      {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, value, icon }: { label: string; value: string | null | undefined; icon?: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{label}</dt>
      <dd className="flex items-center gap-1.5 text-sm">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        {value || <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2: RECOMMENDATIONS
// ═══════════════════════════════════════════════════════════════════════════════

function RecommendationsTab({
  pendingRecs,
  activeRecs,
  shortlistedRecs,
  downvotedRecs,
  spaceCache,
  currentStatus,
  onRemoveRec,
  onUpdateNote,
  onPublish,
  isPublishing,
  shortlist,
  shortlistSpaces,
  onReorderPending,
  isReordering,
}: {
  pendingRecs: RevelRec[]
  activeRecs: RevelRec[]
  shortlistedRecs: RevelRec[]
  downvotedRecs: RevelRec[]
  spaceCache: Map<number, ResolvedSpace>
  currentStatus: RevelRecsAdminStatus
  onRemoveRec: (recId: number) => void
  onUpdateNote: (recId: number, note: string) => void
  onPublish: () => void
  isPublishing: boolean
  shortlist: ShortlistItem[]
  shortlistSpaces: Map<number, { name: string; venueName: string; city: string }>
  onReorderPending: (orderedIds: number[]) => void
  isReordering: boolean
}) {
  const [openDownvoted, setOpenDownvoted] = useState(false)

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      {/* Status display (derived, not editable) */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="border-b bg-muted/20 px-5 py-3">
          <h2 className="text-sm font-semibold">Recs Status</h2>
        </div>
        <div className="p-5">
          <StatusPill status={currentStatus} />
          <p className="mt-2 text-xs text-muted-foreground">
            {currentStatus === 'published'
              ? 'Recommendations are visible to the client.'
              : currentStatus === 'in_progress'
              ? 'Brief submitted. Add recommendations below, then publish when ready.'
              : 'Brief submitted — work not yet started.'}
          </p>
        </div>
      </div>

      {/* Pending recs (not yet visible to client) */}
      {pendingRecs.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/30 overflow-hidden">
          <div className="border-b border-amber-200 bg-amber-50 px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-amber-600" />
                <h2 className="text-sm font-semibold text-amber-800">Pending Recommendations</h2>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[11px]">
                  {pendingRecs.length}
                </Badge>
              </div>
              <Button
                size="sm"
                onClick={onPublish}
                disabled={isPublishing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              >
                {isPublishing ? 'Publishing...' : `Publish ${pendingRecs.length} rec${pendingRecs.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-amber-600">
              These are not visible to the client yet. Click Publish when ready.
            </p>
          </div>
          <div className="p-5">
            <SortableRecsList
              recs={pendingRecs}
              onReorder={onReorderPending}
              disabled={isReordering}
              renderItem={(rec, drag) => (
                <div
                  ref={drag.setNodeRef}
                  style={drag.style}
                  className="flex items-start gap-2"
                >
                  <button
                    {...drag.attributes}
                    {...drag.listeners}
                    className="mt-3 flex-shrink-0 cursor-grab touch-none text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
                    aria-label="Drag to reorder"
                  >
                    <Grip className="size-4" />
                  </button>
                  <div className="flex-1">
                    <ActiveRecCard
                      rec={rec}
                      index={pendingRecs.findIndex((r) => r.id === rec.id) + 1}
                      space={spaceCache.get(rec.space_id)}
                      onRemove={() => onRemoveRec(rec.id)}
                      onUpdateNote={(note) => onUpdateNote(rec.id, note)}
                    />
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* Active recs (visible to client) */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <div className="border-b bg-muted/20 px-5 py-3">
          <div className="flex items-center gap-2">
            <Sparkles className="size-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold">Published Recommendations</h2>
            {activeRecs.length > 0 && (
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[11px]">
                {activeRecs.length}
              </Badge>
            )}
          </div>
        </div>
        <div className="p-5">
          {activeRecs.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {pendingRecs.length > 0
                ? 'No published recommendations yet. Publish pending recs above.'
                : 'No recommendations. Use the Search tab to add venues.'}
            </p>
          ) : (
            <div className="space-y-3">
              {activeRecs.map((rec, idx) => (
                <ActiveRecCard
                  key={rec.id}
                  rec={rec}
                  index={idx + 1}
                  space={spaceCache.get(rec.space_id)}
                  onRemove={() => onRemoveRec(rec.id)}
                  onUpdateNote={(note) => onUpdateNote(rec.id, note)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Shortlisted Recs (client added our rec to their shortlist) */}
      {shortlistedRecs.length > 0 && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 overflow-hidden">
          <div className="border-b border-emerald-200 bg-emerald-50 px-5 py-3">
            <div className="flex items-center gap-2">
              <Star className="size-4 text-emerald-600" />
              <h2 className="text-sm font-semibold text-emerald-800">Added to Shortlist</h2>
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[11px]">
                {shortlistedRecs.length}
              </Badge>
            </div>
            <p className="mt-1 text-[11px] text-emerald-600">
              Recommendations the client has added to their shortlist.
            </p>
          </div>
          <div className="p-5 space-y-2">
            {shortlistedRecs.map((rec) => {
              const space = spaceCache.get(rec.space_id)
              return (
                <div key={rec.id} className="flex items-center gap-3 rounded-lg border border-emerald-100 bg-white p-3">
                  <div className="size-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {space?.imageUrl ? (
                      <img src={space.imageUrl} alt="" className="size-full object-cover" />
                    ) : (
                      <MapPin className="size-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {space?.venueName || space?.name || `Space #${rec.space_id}`}
                      {space?.venueName && space?.name && space.name !== space.venueName && (
                        <span className="font-normal text-muted-foreground"> – {space.name}</span>
                      )}
                    </p>
                    {space?.city && <p className="text-xs text-muted-foreground">{space.city}</p>}
                    {rec.concierge_note && (
                      <p className="text-xs text-muted-foreground mt-0.5 italic">{rec.concierge_note}</p>
                    )}
                  </div>
                  {rec.shortlisted_at && (
                    <span className="text-[11px] text-emerald-600 flex-shrink-0">
                      Shortlisted {new Date(rec.shortlisted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Client Shortlist */}
      {shortlist.length > 0 && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="border-b bg-muted/20 px-5 py-3">
            <div className="flex items-center gap-2">
              <Bookmark className="size-4 text-blue-500" />
              <h2 className="text-sm font-semibold">Client Shortlist</h2>
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[11px]">
                {shortlist.length}
              </Badge>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Spaces the client has shortlisted — excluded from search results.
            </p>
          </div>
          <div className="p-5 space-y-2">
            {shortlist.map((item) => {
              const space = item.space_id != null ? shortlistSpaces.get(item.space_id) : undefined
              return (
                <div key={item.id} className="flex items-center gap-3 rounded-lg border border-blue-100 bg-blue-50/30 p-3">
                  <div className="flex size-9 items-center justify-center rounded-lg bg-blue-50 flex-shrink-0">
                    <Bookmark className="size-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{space?.venueName || `Space #${item.space_id}`}</p>
                    {space?.name && space.name !== space.venueName && (
                      <p className="text-xs text-muted-foreground">{space.name}</p>
                    )}
                    {space?.city && <p className="text-xs text-muted-foreground">{space.city}</p>}
                  </div>
                  {item.created_at && (
                    <span className="text-[11px] text-muted-foreground flex-shrink-0">
                      {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Downvoted recs */}
      {downvotedRecs.length > 0 && (
        <Collapsible open={openDownvoted} onOpenChange={setOpenDownvoted}>
          <div className="rounded-xl border bg-card overflow-hidden">
            <CollapsibleTrigger className="flex w-full items-center justify-between border-b bg-muted/20 px-5 py-3 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold">Downvoted Recs</h2>
                <Badge variant="outline" className="text-[11px]">{downvotedRecs.length}</Badge>
              </div>
              {openDownvoted ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-5 space-y-2">
                {downvotedRecs.map((rec) => {
                  const space = spaceCache.get(rec.space_id)
                  return (
                    <div key={rec.id} className="flex items-center gap-3 rounded-lg border border-red-100 bg-red-50/30 p-3">
                      <div className="size-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {space?.imageUrl ? (
                          <img src={space.imageUrl} alt="" className="size-full object-cover" />
                        ) : (
                          <MapPin className="size-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {space?.venueName || space?.name || `Space #${rec.space_id}`}
                          {space?.venueName && space?.name && space.name !== space.venueName && (
                            <span className="font-normal text-muted-foreground"> – {space.name}</span>
                          )}
                        </p>
                        {rec.downvote_reason && (
                          <p className="text-xs text-red-600 mt-0.5">Reason: {rec.downvote_reason}</p>
                        )}
                      </div>
                      {rec.downvoted_at && (
                        <span className="text-[11px] text-muted-foreground flex-shrink-0">
                          {new Date(rec.downvoted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}
    </div>
  )
}

// ── Active rec card ──────────────────────────────────────────────────────────

function ActiveRecCard({ rec, index, space, onRemove, onUpdateNote }: {
  rec: RevelRec
  index: number
  space?: ResolvedSpace
  onRemove: () => void
  onUpdateNote: (note: string) => void
}) {
  const [note, setNote] = useState(rec.concierge_note || '')
  const [isEditing, setIsEditing] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const handleNoteChange = (value: string) => {
    setNote(value)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => onUpdateNote(value), 800)
  }

  return (
    <div className="group rounded-lg border p-4 transition-colors hover:bg-accent/20">
      <div className="flex items-start gap-3">
        <span className="flex size-7 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700 flex-shrink-0 mt-0.5">
          {index}
        </span>

        <div className="size-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
          {space?.imageUrl ? (
            <img src={space.imageUrl} alt="" className="size-full object-cover" />
          ) : (
            <MapPin className="size-4 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">
              {space?.venueName || space?.name || `Space #${rec.space_id}`}
              {space?.venueName && space?.name && space.name !== space.venueName && (
                <span className="font-normal text-muted-foreground"> – {space.name}</span>
              )}
            </p>
            {space?.city && <span className="text-xs text-muted-foreground">{space.city}</span>}
          </div>

          {/* Concierge note */}
          {isEditing ? (
            <div className="mt-2">
              <Textarea
                value={note}
                onChange={(e) => handleNoteChange(e.target.value)}
                onBlur={() => setIsEditing(false)}
                placeholder="Add a concierge note..."
                rows={2}
                className="resize-none text-sm"
                autoFocus
              />
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="mt-1.5 text-left text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {note ? (
                <span className="italic">{note}</span>
              ) : (
                <span className="text-muted-foreground/50">+ Add concierge note</span>
              )}
            </button>
          )}

          <p className="mt-1 text-[11px] text-muted-foreground">
            Added {new Date(rec.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          onClick={onRemove}
        >
          <Trash2 className="size-3.5" />
        </Button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3: SEARCH VENUES
// ═══════════════════════════════════════════════════════════════════════════════

function SearchTab({ event, activeRecSpaceIds, downvotedSpaceIds, shortlistSpaceIds, onAddRec, isAddingRec }: {
  event: EventDetailData | null | undefined
  activeRecSpaceIds: Set<number>
  downvotedSpaceIds: Set<number>
  shortlistSpaceIds: Set<number>
  onAddRec: (spaceId: number) => void
  isAddingRec: boolean
}) {
  const hydrate = useListingsStore((s) => s.hydrate)
  const hydratedRef = useRef(false)

  const eventVenueGroupId = event?.venue_group_id

  // Hydrate the listings store with the event's venue group. The listings
  // query is gated on this id — without it the panel hangs on "Loading
  // spaces..." indefinitely.
  useEffect(() => {
    if (hydratedRef.current) return
    if (typeof eventVenueGroupId !== 'number') return
    hydrate(null, 1, eventVenueGroupId)
    hydratedRef.current = true
  }, [hydrate, eventVenueGroupId])

  // Combine all "already handled" space IDs so they show as shortlisted
  const combinedShortlistedIds = useMemo(() => {
    const set = new Set<number>()
    for (const id of activeRecSpaceIds) set.add(id)
    for (const id of downvotedSpaceIds) set.add(id)
    for (const id of shortlistSpaceIds) set.add(id)
    return set
  }, [activeRecSpaceIds, downvotedSpaceIds, shortlistSpaceIds])

  // Brief context strip data
  const briefContext = useMemo(() => {
    if (!event) return null
    const parts: string[] = []
    if (event.location) parts.push(event.location)
    if (event.num_attendees) parts.push(`${event.num_attendees} guests`)
    if (Array.isArray(event.space_type) && event.space_type.length) parts.push(event.space_type.join(', '))
    if (event.privacy_requirement) parts.push(event.privacy_requirement)
    return parts.length > 0 ? parts : null
  }, [event])

  const handleShortlist = useCallback(async (spaceId: number, _spaceName: string) => {
    onAddRec(spaceId)
  }, [onAddRec])

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} libraries={['places', 'marker']}>
      <div className="flex h-full flex-col overflow-hidden">
        {/* Brief context strip */}
        {briefContext && (
          <div className="flex flex-shrink-0 items-center gap-3 border-b bg-violet-50/50 px-5 py-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-violet-600">Brief:</span>
            <div className="flex items-center gap-2 flex-wrap">
              {briefContext.map((part, i) => (
                <span key={i} className="inline-flex items-center rounded-full bg-violet-100 px-2.5 py-0.5 text-[11px] font-medium text-violet-700">
                  {part}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Three-panel layout matching the events page */}
        <div className="flex flex-1 min-h-0 bg-[#fefbf6]">
          <ListingsFiltersPanel />
          <ListingsResultsPanel
            onShortlist={handleShortlist}
            shortlistedSpaceIds={combinedShortlistedIds}
          />
          <div className="h-full w-full overflow-hidden">
            <ListingsMap debounceMs={500} />
          </div>
        </div>
      </div>
    </APIProvider>
  )
}
